import { FormSelectDataType, BinIcon } from '@systemeio/ui'
import { pricePlanTypeLocoKeys } from 'modules/digital-product/price-plans/constants/price-plan-type-loco-keys'
import { PricePlanRemoveTypeDataInterface } from 'modules/digital-product/price-plans/types/price-plan-interface'
import { SubscriptionIntervalTitles } from 'modules/payment/transactions/enums/subscription-intervals-enum'
import React from 'react'
import { TLocoType } from 'shared/hooks/use-loco-translation'
import PricePlanTooltip from '../components/price-plan-tooltip'
import { PricePlanRemoveTypeEnum } from '../enums/price-plan-remove-type-enum'
import { PricePlanType, PricePlanTypeEnum, SubscriptionIntervalEnum } from '@systemeio/types'

interface PricePlanListingDataInterface {
  id: PricePlanTypeEnum
  caption: string
}

export const getPricePlansTypesData = (t: TLocoType): PricePlanListingDataInterface[] =>
  Object.values(PricePlanTypeEnum).map(type => ({
    id: type,
    caption: t(pricePlanTypeLocoKeys[type]),
  }))

export const getAvailablePricePlansData = ({
  pricePlansList,
  type,
  existingPricePlans,
  inputRef,
  onClickBin,
}: {
  pricePlansList?: PricePlanType[]
  type?: PricePlanTypeEnum
  existingPricePlans?: PricePlanType[]
  inputRef: React.MutableRefObject<HTMLInputElement | null>
  onClickBin: (pricePlan: PricePlanType) => void
}): FormSelectDataType<number>[] | undefined => {
  return pricePlansList
    ?.filter(pricePlan => {
      if (existingPricePlans?.find(existingPricePlan => existingPricePlan.id === pricePlan.id)) {
        return false
      }
      if (!type) {
        return true
      }
      return pricePlan.type === type
    })
    .map(pricePlan => {
      return {
        id: pricePlan.id,
        caption: pricePlan.innerName,
        customCaption: (
          <PricePlanTooltip
            pricePlan={pricePlan}
            label={
              <div className="flex flex-row gap-1">
                <span className="truncate">{pricePlan.innerName}</span>
              </div>
            }
          />
        ),
        additionalRender: (
          <button
            className={'group px-2'}
            // necessary to prevent headless-ui from element selection
            onMouseDown={e => {
              e.stopPropagation()
            }}
            onClick={e => {
              e.stopPropagation()
              if (inputRef.current) {
                inputRef.current.dispatchEvent(
                  new KeyboardEvent('keydown', {
                    key: 'Escape',
                    code: 'Escape',
                    bubbles: true,
                    cancelable: true,
                  }),
                )
              }
              onClickBin(pricePlan)
            }}
          >
            <BinIcon className="group-hover:fill-red h-4 w-4" />
          </button>
        ),
      }
    })
}

export const isLimitedSubscription = (type?: PricePlanTypeEnum) =>
  type === PricePlanTypeEnum.limited_subscription

export const getSubscriptionIntervalData = (t: TLocoType) =>
  Object.keys(SubscriptionIntervalTitles).map(interval => {
    const type = interval as keyof typeof SubscriptionIntervalEnum
    return {
      id: type,
      caption: t(SubscriptionIntervalTitles[type]),
    }
  }) as { id: SubscriptionIntervalEnum; caption: string }[]

export const getPricePlanRemoveTypeData = (t: TLocoType): PricePlanRemoveTypeDataInterface[] => [
  {
    id: PricePlanRemoveTypeEnum.from_offer,
    caption: t('dashboard.funnel.configuration.common.price_plan.remove_type.from_offer'),
  },
  {
    id: PricePlanRemoveTypeEnum.from_account,
    caption: t('dashboard.funnel.configuration.common.price_plan.remove_type.from_account'),
  },
]
